import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useBranding } from '../contexts/BrandingContext';
import { useAuth } from '../contexts/AuthContext';
import {
  FileText, Plus, Loader2, X, Search, Download, Mail, CheckCircle2,
  Trash2, AlertCircle, Truck, Pencil,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FastAPI validation errors put an array of objects in `detail`. Rendering that
// straight into a toast/JSX crashes React ("Objects are not valid as a React
// child"), so always coerce to a readable string.
const parseErr = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || (typeof x === 'string' ? x : '')).filter(Boolean).join('; ') || fallback;
  if (d && typeof d === 'object') return d.msg || fallback;
  return fallback;
};

const STATUS_META = {
  draft:   { label: 'Draft',   color: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent:    { label: 'Sent',    color: 'bg-blue-100 text-blue-700 border-blue-200' },
  paid:    { label: 'Paid',    color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-200' },
};

const EMAIL_STATUS_META = {
  sent:        { label: 'Email sent',        color: 'bg-blue-50 text-blue-700 border-blue-200' },
  delivered:   { label: 'Delivered',         color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  opened:      { label: 'Opened',            color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  clicked:     { label: 'Clicked',           color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  delayed:     { label: 'Delayed',           color: 'bg-amber-50 text-amber-700 border-amber-200' },
  bounced:     { label: 'Bounced',           color: 'bg-red-50 text-red-700 border-red-200' },
  complained:  { label: 'Spam complaint',    color: 'bg-red-50 text-red-700 border-red-200' },
};

const InvoiceEmailPill = ({ invoice }) => {
  const s = invoice?.last_email_status;
  if (!s) return null;
  const meta = EMAIL_STATUS_META[s] || { label: s, color: 'bg-slate-50 text-slate-600 border-slate-200' };
  const at = invoice.last_email_status_at;
  return (
    <span
      data-testid={`invoice-email-status-${invoice.id}-${s}`}
      title={`Email ${s}${invoice.last_email_to ? ' • ' + invoice.last_email_to : ''}${at ? ' • ' + new Date(at).toLocaleString() : ''}`}
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${meta.color}`}
    >
      {meta.label}
    </span>
  );
};

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';

const Invoices = () => {
  const { branding } = useBranding();
  const { user } = useAuth();
  const isAdmin = user?.role === 'company_admin' || user?.role === 'super_admin';
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null); // null | {} (new) | invoice (edit)
  const [autoDraft, setAutoDraft] = useState(null);
  useEffect(() => {
    axios.get(`${API}/invoices/settings`, { withCredentials: true }).then((r) => setAutoDraft(r.data?.auto_draft_on_delivery !== false)).catch(() => {});
  }, []);
  const toggleAutoDraft = async () => {
    try {
      const r = await axios.put(`${API}/invoices/settings`, { auto_draft_on_delivery: !autoDraft }, { withCredentials: true });
      setAutoDraft(r.data.auto_draft_on_delivery);
      toast.success(r.data.auto_draft_on_delivery ? 'Invoices will be drafted automatically on delivery' : 'Auto-draft on delivery turned off');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
  };

  // Business details that must appear on every outgoing invoice. If any are
  // blank the "From" block on the PDF/public page will look unprofessional.
  const missingBusinessFields = useMemo(() => {
    const missing = [];
    if (!branding?.address_line1) missing.push('Street address');
    if (!branding?.city) missing.push('City');
    if (!branding?.state) missing.push('State');
    if (!branding?.zip_code) missing.push('ZIP code');
    if (!branding?.phone) missing.push('Phone');
    return missing;
  }, [branding]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/invoices`, { withCredentials: true });
      setInvoices(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const filtered = useMemo(() => invoices.filter((i) => {
    if (statusFilter !== 'all' && i.status !== statusFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return (i.invoice_number || '').toLowerCase().includes(q) ||
           (i.customer?.name || '').toLowerCase().includes(q);
  }), [invoices, statusFilter, query]);

  const counts = useMemo(() => ({
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    outstanding: invoices.filter(i => i.status !== 'paid').reduce((s, i) => s + Number(i.total || 0), 0),
  }), [invoices]);

  const downloadPdf = async (inv) => {
    try {
      const r = await axios.get(`${API}/invoices/${inv.id}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const sendEmail = async (inv) => {
    if (!inv.customer?.email) { toast.error('Customer has no email on file'); return; }
    if (!window.confirm(`Email invoice ${inv.invoice_number} to ${inv.customer.email}?`)) return;
    try {
      const r = await axios.post(`${API}/invoices/${inv.id}/send`, {}, { withCredentials: true });
      toast.success(`Sent to ${r.data.to}`);
      fetchInvoices();
    } catch (e) { toast.error(parseErr(e, 'Send failed')); }
  };

  const markPaid = async (inv) => {
    if (!window.confirm(`Mark ${inv.invoice_number} as paid?`)) return;
    try {
      await axios.post(`${API}/invoices/${inv.id}/mark-paid`, {}, { withCredentials: true });
      toast.success('Marked as paid');
      fetchInvoices();
    } catch (e) { toast.error(parseErr(e, 'Failed')); }
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}? Attached loads will be released.`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`, { withCredentials: true });
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (e) { toast.error(parseErr(e, 'Failed')); }
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="invoices-page">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#002FA7]/10 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#002FA7]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Invoices</h1>
              <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Bill brokers and customers — pull from completed loads or add line items manually.</p>
              {autoDraft !== null && (
                <label className="mt-1 inline-flex items-center gap-2 text-xs text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <input type="checkbox" className="h-3.5 w-3.5 accent-[#002FA7]" checked={autoDraft} onChange={toggleAutoDraft} data-testid="invoice-auto-draft-toggle" />
                  Auto-draft an invoice (broker, rate, references) the moment a load is delivered
                </label>
              )}
            </div>
          </div>
          <button
            data-testid="new-invoice-btn"
            onClick={() => setEditing({})}
            className="flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white rounded-md hover:bg-[#00247D] transition-colors font-semibold text-sm shadow-sm"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <Plus className="w-4 h-4" /> New invoice
          </button>
        </div>

        {missingBusinessFields.length > 0 && (
          <div
            data-testid="business-details-banner"
            className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3 bg-amber-50 border border-amber-200 rounded-md p-3 sm:p-4"
          >
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Your business details are incomplete.
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                The &quot;From&quot; block on every invoice pulls from Company Branding. Missing: {missingBusinessFields.join(', ')}.
              </p>
            </div>
            <Link
              to="/company/branding"
              data-testid="banner-finish-branding"
              className={`self-start sm:self-auto text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-md px-3 py-2 whitespace-nowrap ${isAdmin ? '' : 'hidden'}`}
            >
              Finish business details
            </Link>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[['all', counts.total, 'Total'], ['draft', counts.draft, 'Draft'], ['sent', counts.sent, 'Sent'], ['paid', counts.paid, 'Paid'], ['overdue', counts.overdue, 'Overdue']].map(([key, n, label]) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setStatusFilter(key)}
              className={`text-left bg-white border rounded-md p-3 transition ${statusFilter === key ? 'border-[#002FA7] ring-1 ring-[#002FA7]' : 'border-slate-200 hover:border-slate-300'}`}
            >
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
              <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{n}</p>
            </button>
          ))}
        </div>
        <p className="text-sm text-slate-600 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Outstanding (not paid): <strong className="text-[#002FA7]">{fmtUSD(counts.outstanding)}</strong>
        </p>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid="invoice-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search invoice # or customer…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
            />
          </div>
          {loading ? (
            <div className="px-6 py-10 flex items-center justify-center text-slate-500 text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          ) : filtered.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-slate-500">No invoices match.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-2 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Invoice</th>
                    <th className="text-left py-2 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Customer</th>
                    <th className="text-left py-2 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Issued</th>
                    <th className="text-left py-2 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Due</th>
                    <th className="text-right py-2 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Total</th>
                    <th className="text-left py-2 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Status</th>
                    <th className="text-right py-2 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((inv) => {
                    const meta = STATUS_META[inv.status] || STATUS_META.draft;
                    return (
                      <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="py-3 px-4 font-semibold text-slate-900">{inv.invoice_number}</td>
                        <td className="py-3 px-4 text-slate-700">{inv.customer?.name}</td>
                        <td className="py-3 px-4 text-slate-600">{fmtDate(inv.issue_date)}</td>
                        <td className="py-3 px-4 text-slate-600">{fmtDate(inv.due_date)}</td>
                        <td className="py-3 px-4 text-right font-semibold text-slate-900">{fmtUSD(inv.total)}</td>
                        <td className="py-3 px-4"><span data-testid={`status-${inv.id}`} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${meta.color}`}>{meta.label}</span>{inv.auto_drafted && inv.status === 'draft' && <span data-testid={`auto-drafted-${inv.id}`} className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-[#002FA7]/10 text-[#002FA7]" title="Pre-filled from the delivered load — review and send">Auto-drafted</span>}{inv.last_email_status && <div className="mt-1"><InvoiceEmailPill invoice={inv} /></div>}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex gap-1 flex-wrap justify-end">
                            {inv.status === 'draft' && (
                              <button data-testid={`edit-${inv.id}`} onClick={() => setEditing(inv)} className="px-2 py-1 rounded text-xs text-slate-600 hover:bg-slate-100" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                            )}
                            <button data-testid={`download-${inv.id}`} onClick={() => downloadPdf(inv)} className="px-2 py-1 rounded text-xs text-[#002FA7] hover:bg-blue-50" title="Download PDF"><Download className="w-3.5 h-3.5" /></button>
                            <button data-testid={`send-${inv.id}`} onClick={() => sendEmail(inv)} className="px-2 py-1 rounded text-xs text-slate-700 hover:bg-slate-100" title="Email to customer"><Mail className="w-3.5 h-3.5" /></button>
                            {inv.status !== 'paid' && (
                              <button data-testid={`mark-paid-${inv.id}`} onClick={() => markPaid(inv)} className="px-2 py-1 rounded text-xs text-emerald-700 hover:bg-emerald-50" title="Mark paid"><CheckCircle2 className="w-3.5 h-3.5" /></button>
                            )}
                            {inv.status !== 'paid' && (
                              <button data-testid={`delete-${inv.id}`} onClick={() => deleteInvoice(inv)} className="px-2 py-1 rounded text-xs text-red-700 hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing !== null && (
        <InvoiceEditor
          invoice={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchInvoices(); }}
        />
      )}
    </DashboardLayout>
  );
};

// ============================================================================
// Editor modal — handles both create & edit
// ============================================================================
const InvoiceEditor = ({ invoice, onClose, onSaved }) => {
  const isEdit = !!invoice;
  const [customers, setCustomers] = useState({ saved: [], brokers: [] });
  const [unbilled, setUnbilled] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);

  // Form state
  const [customer, setCustomer] = useState(invoice?.customer || { name: '', email: '', address: '', phone: '' });
  const [taxRate, setTaxRate] = useState(invoice?.tax_rate || 0);
  const [issueDate, setIssueDate] = useState(invoice?.issue_date || new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(invoice?.due_date || '');
  const [poNumber, setPoNumber] = useState(invoice?.po_number || '');
  const [notes, setNotes] = useState(invoice?.notes || '');
  const [lineItems, setLineItems] = useState(invoice?.line_items?.length ? invoice.line_items : [{ description: '', quantity: 1, rate: 0 }]);
  const [loadPickerOpen, setLoadPickerOpen] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, u, s] = await Promise.all([
          axios.get(`${API}/invoices/customers`, { withCredentials: true }),
          axios.get(`${API}/invoices/unbilled-loads`, { withCredentials: true }),
          axios.get(`${API}/invoices/settings`, { withCredentials: true }),
        ]);
        setCustomers(c.data || { saved: [], brokers: [] });
        setUnbilled(u.data || []);
        // Pre-fill the reusable default note on brand-new invoices only.
        if (!isEdit && !invoice?.notes && s.data?.default_note) {
          setNotes(s.data.default_note);
        }
      } catch (e) {
        toast.error('Failed to load form data');
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, []);

  const saveAsDefaultNote = async () => {
    setSavingDefault(true);
    try {
      await axios.put(`${API}/invoices/settings`, { default_note: notes || null }, { withCredentials: true });
      toast.success(notes?.trim() ? 'Saved as default note for future invoices' : 'Default note cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save default note');
    } finally {
      setSavingDefault(false);
    }
  };

  const subtotal = lineItems.reduce((s, li) => s + (Number(li.quantity || 0) * Number(li.rate || 0)), 0);
  const taxAmt = subtotal * (Number(taxRate || 0) / 100);
  const total = subtotal + taxAmt;

  const pickCustomer = (name) => {
    const saved = customers.saved.find(s => s.name === name);
    if (saved) {
      setCustomer({ name: saved.name, email: saved.email || '', address: saved.address || '', phone: saved.phone || '', customer_id: saved.id });
    } else {
      // It's a broker name — just set the name, user can fill the rest.
      setCustomer({ name, email: '', address: '', phone: '' });
    }
  };

  const addManualLine = () => setLineItems([...lineItems, { description: '', quantity: 1, rate: 0 }]);
  const removeLine = (i) => setLineItems(lineItems.filter((_, idx) => idx !== i));
  const updateLine = (i, field, value) => setLineItems(lineItems.map((li, idx) => idx === i ? { ...li, [field]: value } : li));

  const addLoadAsLine = (ld) => {
    const desc = `${ld.load_ref ? `Load ${ld.load_ref} — ` : ''}${ld.pickup} → ${ld.delivery}`;
    setLineItems([
      ...lineItems.filter(li => li.description || li.rate),
      { load_id: ld.id, description: desc, quantity: 1, rate: ld.load_rate },
    ]);
    setUnbilled(unbilled.filter(u => u.id !== ld.id));
  };

  const save = async () => {
    if (!customer.name?.trim()) { toast.error('Customer name is required'); return; }
    const cleanItems = lineItems
      .filter(li => li.description?.trim() && Number(li.rate || 0) >= 0)
      .map(li => ({
        ...(li.load_id ? { load_id: li.load_id } : {}),
        description: li.description.trim(),
        quantity: Number(li.quantity || 1),
        rate: Number(li.rate || 0),
      }));
    if (cleanItems.length === 0) { toast.error('Add at least one line item'); return; }
    // Strip empty strings — the backend validates email as a real address, so
    // an empty "" would 422. Send null / omit instead.
    const cleanCustomer = {
      name: customer.name.trim(),
      email: customer.email?.trim() || null,
      phone: customer.phone?.trim() || null,
      address: customer.address?.trim() || null,
      ...(customer.customer_id ? { customer_id: customer.customer_id } : {}),
    };
    setBusy(true);
    try {
      const payload = {
        customer: cleanCustomer,
        line_items: cleanItems,
        tax_rate: Number(taxRate || 0),
        issue_date: issueDate,
        due_date: dueDate || null,
        po_number: poNumber || null,
        notes: notes || null,
      };
      if (isEdit) {
        await axios.put(`${API}/invoices/${invoice.id}`, payload, { withCredentials: true });
        toast.success('Invoice updated');
      } else {
        await axios.post(`${API}/invoices`, payload, { withCredentials: true });
        toast.success('Invoice created');
      }
      onSaved();
    } catch (e) {
      toast.error(parseErr(e, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-2 sm:p-4" data-testid="invoice-editor">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[95vh] flex flex-col shadow-2xl overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {isEdit ? `Edit ${invoice.invoice_number}` : 'New Invoice'}
          </h2>
          <button onClick={onClose} data-testid="editor-close" className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-5">
          {loadingMeta ? (
            <div className="py-8 flex items-center justify-center text-slate-500 gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
          ) : (
          <>
          {/* Customer block */}
          <section>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Bill To</h3>
            {!isEdit && (customers.saved.length > 0 || customers.brokers.length > 0) && (
              <div className="mb-3">
                <label className="text-xs text-slate-600 mb-1 block">Pick from existing</label>
                <select
                  data-testid="customer-picker"
                  onChange={(e) => e.target.value && pickCustomer(e.target.value)}
                  defaultValue=""
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                >
                  <option value="">— select a saved customer or broker —</option>
                  {customers.saved.length > 0 && <optgroup label="Saved customers">
                    {customers.saved.map(c => <option key={c.id} value={c.name}>{c.name}{c.email ? ` (${c.email})` : ''}</option>)}
                  </optgroup>}
                  {customers.brokers.length > 0 && <optgroup label="From loads (brokers)">
                    {customers.brokers.map(b => <option key={b} value={b}>{b}</option>)}
                  </optgroup>}
                </select>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input data-testid="cust-name" placeholder="Customer name *" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
              <input data-testid="cust-email" placeholder="Email" type="email" value={customer.email || ''} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
              <input data-testid="cust-phone" placeholder="Phone" value={customer.phone || ''} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
              <input data-testid="cust-address" placeholder="Address" value={customer.address || ''} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} className="px-3 py-2 border border-slate-300 rounded-md text-sm" />
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">New customers are saved automatically for future invoices.</p>
          </section>

          {/* Line items */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">Line Items</h3>
              <div className="flex gap-2">
                <button data-testid="add-from-loads" onClick={() => setLoadPickerOpen(true)} type="button" className="text-xs font-semibold text-[#002FA7] hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1"><Truck className="w-3 h-3" /> Add from loads</button>
                <button data-testid="add-manual-line" onClick={addManualLine} type="button" className="text-xs font-semibold text-slate-700 hover:bg-slate-100 px-2 py-1 rounded flex items-center gap-1"><Plus className="w-3 h-3" /> Add line</button>
              </div>
            </div>
            <div className="space-y-2">
              {lineItems.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-${i}`}>
                  <input placeholder="Description" value={li.description} onChange={(e) => updateLine(i, 'description', e.target.value)} className="col-span-12 sm:col-span-6 px-2 py-1.5 border border-slate-300 rounded text-sm" />
                  <input placeholder="Qty" type="number" step="0.01" value={li.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} className="col-span-3 sm:col-span-2 px-2 py-1.5 border border-slate-300 rounded text-sm" />
                  <input placeholder="Rate" type="number" step="0.01" value={li.rate} onChange={(e) => updateLine(i, 'rate', e.target.value)} className="col-span-4 sm:col-span-2 px-2 py-1.5 border border-slate-300 rounded text-sm" />
                  <div className="col-span-4 sm:col-span-1 text-right text-sm font-semibold">{fmtUSD(Number(li.quantity || 0) * Number(li.rate || 0))}</div>
                  <button onClick={() => removeLine(i)} type="button" className="col-span-1 text-red-500 hover:bg-red-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  {li.load_id && <p className="col-span-12 -mt-1 text-[10px] text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Linked to load</p>}
                </div>
              ))}
            </div>
          </section>

          {/* Meta */}
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <label className="text-xs text-slate-600">Issue date<input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} data-testid="issue-date" className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm" /></label>
            <label className="text-xs text-slate-600">Due date<input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="due-date" className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm" /></label>
            <label className="text-xs text-slate-600">PO #<input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} data-testid="po-number" className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm" /></label>
            <label className="text-xs text-slate-600">Tax %<input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="tax-rate" className="mt-1 w-full px-2 py-1.5 border border-slate-300 rounded text-sm" /></label>
          </section>

          <section>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600 font-medium">Notes (thank-you, payment instructions, etc.)</span>
              <button
                type="button"
                data-testid="save-default-note"
                onClick={saveAsDefaultNote}
                disabled={savingDefault}
                className="text-xs font-semibold text-[#002FA7] hover:underline disabled:opacity-50"
              >
                {savingDefault ? 'Saving…' : 'Save as default'}
              </button>
            </div>
            <textarea
              rows={8}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="invoice-notes"
              placeholder={"Thank you for your business!\n\nPayment Information\nZelle: billing@yourcompany.com\nACH / Bank Transfer\nRouting: 000000000\nAccount: 000000000\n\nPlease include the invoice number with your payment."}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded text-sm whitespace-pre-wrap font-mono"
            />
            <p className="text-[11px] text-slate-400 mt-1">Line breaks are preserved on the invoice PDF and the customer's online invoice. "Save as default" reuses this on every new invoice.</p>
          </section>

          {/* Totals preview */}
          <section className="border-t border-slate-100 pt-3 text-right text-sm space-y-1">
            <p>Subtotal <span className="ml-3 font-semibold text-slate-900">{fmtUSD(subtotal)}</span></p>
            {Number(taxRate) > 0 && <p>Tax ({taxRate}%) <span className="ml-3 font-semibold text-slate-900">{fmtUSD(taxAmt)}</span></p>}
            <p className="text-lg"><strong>Total</strong> <span data-testid="invoice-total" className="ml-3 font-bold text-[#002FA7]">{fmtUSD(total)}</span></p>
          </section>
          </>
          )}
        </div>
        <div className="border-t border-slate-100 px-4 sm:px-6 py-3 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="save-invoice" className="px-4 py-2 text-sm font-semibold text-white bg-[#002FA7] hover:bg-[#00247D] rounded-md disabled:opacity-50 flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create invoice'}
          </button>
        </div>
      </div>

      {/* Sub-modal: load picker */}
      {loadPickerOpen && (
        <div className="fixed inset-0 z-[70] bg-slate-900/70 flex items-center justify-center p-2 sm:p-4" onClick={() => setLoadPickerOpen(false)} data-testid="load-picker">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 sm:px-6 py-3 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-bold text-slate-900">Unbilled completed loads</h3>
              <button onClick={() => setLoadPickerOpen(false)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-3">
              {unbilled.length === 0
                ? <p className="text-sm text-slate-500 py-6 text-center flex items-center justify-center gap-2"><AlertCircle className="w-4 h-4" />No unbilled completed loads.</p>
                : <ul className="divide-y divide-slate-100">
                    {unbilled.map((ld) => (
                      <li key={ld.id} className="py-2 flex items-center justify-between gap-3" data-testid={`unbilled-${ld.id}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{ld.load_ref || '—'} {ld.broker && <span className="text-slate-500 font-normal">· {ld.broker}</span>}</p>
                          <p className="text-xs text-slate-600 truncate">{ld.pickup} → {ld.delivery} · {fmtUSD(ld.load_rate)}</p>
                          <p className="text-[11px] text-slate-400">Delivered {fmtDate(ld.delivered_at)}</p>
                        </div>
                        <button data-testid={`add-load-${ld.id}`} onClick={() => addLoadAsLine(ld)} className="text-xs font-semibold text-[#002FA7] hover:bg-blue-50 px-3 py-1.5 rounded border border-[#002FA7]">Add</button>
                      </li>
                    ))}
                  </ul>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
